import { initializeApp } from "firebase/app";
import { getAnalytics } from "firebase/analytics";
import {
  getAuth,
  GoogleAuthProvider,
  signInWithPopup,
  signOut,
  onAuthStateChanged,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  sendEmailVerification,
  sendPasswordResetEmail,
  updateProfile,
  reload,
} from "firebase/auth";
import {
  getFirestore,
  doc,
  getDoc,
  setDoc,
  addDoc,
  collection,
  query,
  where,
  getDocs,
  serverTimestamp,
} from "firebase/firestore";

const firebaseConfig = {
  apiKey:            process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain:        process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  databaseURL:       process.env.REACT_APP_FIREBASE_DATABASE_URL,
  projectId:         process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket:     process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId:             process.env.REACT_APP_FIREBASE_APP_ID,
  measurementId:     process.env.REACT_APP_FIREBASE_MEASUREMENT_ID,
};

const app      = initializeApp(firebaseConfig);
export const analytics = getAnalytics(app);
export const auth      = getAuth(app);
export const db        = getFirestore(app);

const googleProvider = new GoogleAuthProvider();
googleProvider.setCustomParameters({ prompt: "select_account" });

/** Signs in with Google popup. Returns the Firebase user object. */
export async function signInWithGoogle() {
  const result = await signInWithPopup(auth, googleProvider);
  return result.user; // { displayName, email, photoURL, uid, … }
}

/**
 * Creates an email/password account and sends the verification email.
 * The account exists immediately but cannot track or hold an API key until the
 * address is confirmed, so callers should show "check your inbox" rather than
 * treating this as a completed sign-in.
 */
export async function signUpWithEmail(email, password, displayName) {
  const { user } = await createUserWithEmailAndPassword(auth, email, password);
  if (displayName) {
    await updateProfile(user, { displayName });
  }
  await sendEmailVerification(user);
  return user;
}

/** Signs in with an email address and password. */
export async function signInWithEmail(email, password) {
  const { user } = await signInWithEmailAndPassword(auth, email, password);
  return user;
}

/** Re-sends the verification email to the signed-in user. */
export async function resendVerificationEmail() {
  if (!auth.currentUser) throw new Error('Not signed in.');
  await sendEmailVerification(auth.currentUser);
}

/** Sends a password reset email. */
export async function sendPasswordReset(email) {
  await sendPasswordResetEmail(auth, email);
}

/**
 * Re-fetches the user from Firebase to pick up a verification that happened in
 * another tab, and returns whether the address is now verified.
 */
export async function refreshEmailVerification() {
  if (!auth.currentUser) return false;
  await reload(auth.currentUser);
  return auth.currentUser.emailVerified;
}

/**
 * Returns a current Firebase ID token for the signed-in user, or null.
 * The SDK refreshes it automatically when it is close to expiry.
 */
export async function getIdToken(forceRefresh = false) {
  if (!auth.currentUser) return null;
  return auth.currentUser.getIdToken(forceRefresh);
}

/** Signs out of Firebase. */
export async function firebaseSignOut() {
  await signOut(auth);
}

/** Subscribes to Firebase Auth state. Returns unsubscribe fn. */
export function onFirebaseAuthStateChanged(callback) {
  return onAuthStateChanged(auth, callback);
}

// ── Firestore helpers ────────────────────────────────────────────────────────

export async function getUserDoc(uid) {
  return getDoc(doc(db, 'users', uid));
}

export async function setUserDoc(uid, data) {
  return setDoc(doc(db, 'users', uid), data, { merge: true });
}

export async function getUserProfile(uid) {
  return getDoc(doc(db, 'userTrackingProfile', uid));
}

export async function setUserProfile(uid, data) {
  return setDoc(doc(db, 'userTrackingProfile', uid), data, { merge: true });
}

export async function saveTrackingRequest(uid, trackingNumber) {
  return addDoc(collection(db, 'trackingRequests'), {
    userId: uid,
    trackingNumber,
    createdAt: serverTimestamp(),
  });
}

export async function getTrackingRequests(uid) {
  const q = query(
    collection(db, 'trackingRequests'),
    where('userId', '==', uid)
  );
  const snap = await getDocs(q);
  const docs = snap.docs.map(d => ({ id: d.id, ...d.data() }));
  // Sort client-side to avoid requiring a composite Firestore index
  return docs.sort((a, b) => {
    const ta = a.createdAt?._seconds ?? 0;
    const tb = b.createdAt?._seconds ?? 0;
    return tb - ta;
  });
}
